import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { Tenant } from "@bonliva-crm/shared";

// Define the context type
interface TenantContextType {
  tenant: Tenant;
  setTenant: (tenantCode: string) => void;
}

// Create the context with default values
const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Local storage keys
export const CURRENT_TENANT_KEY = "crm.tenant";
const DEFAULT_TENANT = Tenant.BonlivaAB;
const tenants = Object.values(Tenant);

export function getCurrentTenant(): Tenant {
  const tenant = localStorage.getItem(CURRENT_TENANT_KEY) as Tenant;

  return tenant ?? DEFAULT_TENANT;
}

// Props for the provider component
interface TenantProviderProps {
  children: ReactNode;
  initialTenant?: Tenant;
}

// Create the provider component
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant>(getCurrentTenant());

  // Listen for tenant changes from outside React
  useEffect(() => {
    const handleTenantChanged = () => {
      const currentTenant = getCurrentTenant();
      setCurrentTenant(currentTenant);
      document.location.reload();
    };

    window.addEventListener("tenantChanged", handleTenantChanged);

    return () => {
      window.removeEventListener("tenantChanged", handleTenantChanged);
    };
  }, [currentTenant]);

  // Context value
  const value: TenantContextType = {
    tenant: currentTenant,
    setTenant: (tenantCode: Tenant) => {
      // Find the tenant with matching code in the tenants list
      const tenant = tenants.find((t) => t === tenantCode);

      if (tenant) {
        localStorage.setItem(CURRENT_TENANT_KEY, tenant);
        window.dispatchEvent(new Event("tenantChanged"));
      }
    }
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

// Custom hook to use the tenant context
export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}
